import { CheckoutPreparation } from '../checkout-preparation.js';
import { openNativePro } from '../native-pro-dialog.js';
import { satellitePopupAssignment } from '../satellite-popup-experiment.js';
import type { SatellitePopupAttribution } from '../../shared/satellite-popup.js';
import { proCheckoutHeaders } from '../checkout-browser.js';
import { redirectCheckoutResponse } from '../checkout-navigation.js';
import { datasetSaveReturnTo } from '../pro-dataset-save.js';
import { rememberCheckoutReturnTo } from '../checkout-map-draft.js';
import { dismissPrompt } from '../session-dismissals.js';
import { CheckoutRequestError, checkoutFailureReason } from '../checkout-failure.js';
import { checkoutDeadline } from '../checkout-timing.js';
import { createLazyFeature } from '../lazy-feature-loader.js';
const loadEmbeddedCheckoutDialogLitModule = createLazyFeature(() => import('./embedded-checkout-dialog-lit.js'));
import { isFunction, isNumber, isObject, isString } from '../../shared/runtime-values.js';
import { LitElement, type TemplateResult } from 'lit';
import { html } from 'lit/html.js';
import type { UpgradeActions } from '../store/panel-action-contracts.js';
import { t } from '../i18n.js';
import { createCheckoutAnalyticsContext } from '../analytics-context.js';
import type { CheckoutAnalyticsContext } from '../analytics-context.js';
import { getAppOverlayHost } from '../app-overlay-host.js';
import { checkoutDurationMs, warmCheckout, wasCheckoutWarmupRequested } from '../checkout-warmup.js';
import { DEFAULT_CHECKOUT_EXPERIENCE } from '../../shared/checkout-metadata.js';
import type {
  EmbeddedCheckoutDialogElement,
} from './embedded-checkout-dialog-lit.js';
import type { SchemaInput, SchemaRecord } from '../../shared/schema-input.js';
import { requireActions } from './required-actions.js';
import { recordCheckoutCancellation } from '../checkout-cancellation.js';

interface UpgradeCheckoutResponse {
  error?: string;
  ui_mode?: string;
  client_secret?: string;
  publishable_key?: string;
  checkout_attempt_id?: string;
  checkout_cancellation_id?: string;
  checkout_cancellation_token?: string;
  checkout_cancellation_expires_at?: number;
}

interface UpgradeCheckoutProps extends Partial<SatellitePopupAttribution> {
  checkout_mode?: string;
  feature: string;
  source: string;
  user_plan: string;
  coupon_active: string;
  checkout_attempt_id?: string;
  checkout_experience_variant?: string;
}

interface UpgradeCheckoutRequest extends CheckoutAnalyticsContext, Partial<SatellitePopupAttribution> {
  return_to?: string;
  feature: string;
  source: string;
  user_plan: string;
  coupon_active: string;
  checkout_mode: string;
  checkout_experience_variant?: string;
  email?: string;
}

function upgradeCheckoutResponse(value: SchemaInput): UpgradeCheckoutResponse {
  if (!isObject(value) || Array.isArray(value)) return {};
  // SAFETY: The object boundary is checked above, and each accepted field is checked below.
  const record = value as SchemaRecord;
  return {
    error: isString(record.error) ? record.error : undefined,
    ui_mode: isString(record.ui_mode) ? record.ui_mode : undefined,
    client_secret: isString(record.client_secret) ? record.client_secret : undefined,
    publishable_key: isString(record.publishable_key) ? record.publishable_key : undefined,
    checkout_attempt_id: isString(record.checkout_attempt_id) ? record.checkout_attempt_id : undefined,
    checkout_cancellation_id: isString(record.checkout_cancellation_id) ? record.checkout_cancellation_id : undefined,
    checkout_cancellation_token: isString(record.checkout_cancellation_token) ? record.checkout_cancellation_token : undefined,
    checkout_cancellation_expires_at: isNumber(record.checkout_cancellation_expires_at)
      ? record.checkout_cancellation_expires_at
      : undefined,
  };
}

export class UpgradeDialogElement extends LitElement {
  static init(actions: UpgradeActions) {
    const el = new UpgradeDialogElement();
    el.actions = actions;
    getAppOverlayHost().appendChild(el);
    return el;
  }

  static properties: any = {
    satellitePopup: { state: true },
    satelliteVideoFailed: { state: true },
    satelliteVideoPaused: { state: true },
    satelliteVideoScene: { state: true },
    upgradeHasCoupon: { type: Boolean },
    activeFeature: { type: String },
    activeSource: { type: String },
    checkoutActivity: { state: true },
    checkoutStatus: { type: String },
    headlineText: { type: String },
    headlineHidden: { type: Boolean },
  };

  private _actions: UpgradeActions | null = null;
  declare private satellitePopup: SatellitePopupAttribution | null;
  declare private satelliteVideoFailed: boolean;
  declare private satelliteVideoPaused: boolean;
  declare private satelliteVideoScene: number;
  private exposureSequence = 0;
  private exposureObserver: IntersectionObserver | null = null;
  private exposureWatch: AbortController | null = null;
  private videoOutcomes = new Set<string>();
  declare upgradeHasCoupon: boolean;
  declare activeFeature: string;
  declare activeSource: string;
  declare private checkoutActivity: 'idle' | 'checkout';
  get ctaDisabled(): boolean { return this.checkoutActivity === 'checkout'; }
  set ctaDisabled(value: boolean) { this.checkoutActivity = value ? 'checkout' : 'idle'; }
  private _datasetSaveTitle = '';
  private _datasetSaveReturnTo: string | null = null;
  declare checkoutStatus: string;
  declare headlineText: string;
  declare headlineHidden: boolean;
  declare private _dialog: HTMLDialogElement | null;
  declare private _embeddedCheckout: EmbeddedCheckoutDialogElement | null;

  createRenderRoot(): ShadowRoot | this {
    return this;
  }

  constructor() {
    super();
    this.upgradeHasCoupon = false;
    this.satellitePopup = null;
    this.satelliteVideoFailed = false;
    this.satelliteVideoPaused = true;
    this.satelliteVideoScene = 0;
    this.activeFeature = 'map_library';
    this.activeSource = 'upgrade_dialog';
    this.ctaDisabled = false;
    this.checkoutStatus = '';
    this.headlineText = '';
    this.headlineHidden = true;
    this._embeddedCheckout = null;
  }

  set actions(actions: UpgradeActions) {
    this._actions = actions;
  }

  private get requiredActions(): UpgradeActions {
    return requireActions('UpgradeDialogElement', this._actions);
  }

  firstUpdated() {
    this._dialog = this.renderRoot.querySelector('#upgrade-dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.upgradeHasCoupon = false;
  }

  _trackUpgradePromptDismissed() {
    this._cancelDatasetSave();
    if (this.activeFeature === 'satellite_basemap') dismissPrompt('satellite-upgrade');
    const actions = this.requiredActions;
    actions.track('premium_upgrade_prompt_dismissed', {
      ...this.satelliteAttribution,
      feature: this.activeFeature,
      source: this.activeSource,
      user_plan: actions.getUserPlanForAnalytics() || 'anonymous',
      coupon_active: String(this.upgradeHasCoupon)
    });
  }

  close() {
    this.exposureSequence++;
    this.exposureObserver?.disconnect();
    this.exposureWatch?.abort();
    this.renderRoot.querySelector('video')?.pause();
    if (this._dialog) {
      if (isFunction(this._dialog.close)) this._dialog.close();
      this._dialog.removeAttribute('open');
    }
  }

  private _focusTitle(): void {
    this.renderRoot.querySelector<HTMLElement>('#upgrade-dialog-title')?.focus({ preventScroll: true });
  }

  private _watchExposure(record: () => void): void {
    if (!this._dialog) return;
    this.exposureObserver?.disconnect();
    this.exposureWatch?.abort();
    this.exposureWatch = new AbortController();
    document.addEventListener('visibilitychange', record, { signal: this.exposureWatch.signal });
    this.exposureObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) record();
    });
    this.exposureObserver.observe(this._dialog);
  }

  open({ feature = 'map_library', source = 'upgrade_dialog' }: any = {}) {
    if (window.drawOnAMapNative) {
      if (feature === 'service_area_widget') return;
      void openNativePro().then(async active => {
        if (active) window.location.assign(await this.requiredActions.saveCheckoutDraft());
      });
      return;
    }
    if (this._dialog?.open) return;
    this._cancelDatasetSave();
    this.satellitePopup = feature === 'satellite_basemap' ? satellitePopupAssignment() : null;
    this.satelliteVideoFailed = false;
    this.satelliteVideoPaused = true;
    this.satelliteVideoScene = 0;
    this.videoOutcomes.clear();
    warmCheckout();
    void this._embeddedCheckoutElement();
    const actions = this.requiredActions;
    this.activeFeature = feature;
    this.activeSource = source;
    const plan = actions.getUserPlanForAnalytics() || 'anonymous';
    const gateProps = {
      ...this.satelliteAttribution,
      feature,
      source,
      user_plan: plan,
      coupon_active: String(this.upgradeHasCoupon),
      trial: 'false'
    };
    const sequence = ++this.exposureSequence;
    let recorded = false;
    const recordExposure = () => {
      if (recorded || !this._dialog?.open || sequence !== this.exposureSequence || document.visibilityState !== 'visible' || !this._dialog.getBoundingClientRect().width) return;
      recorded = true;
      this.exposureObserver?.disconnect();
      this.exposureWatch?.abort();
      actions.track('premium_gate_viewed', gateProps);
      actions.track('upgrade_prompted', gateProps);
      actions.track('premium_feature_prompted', gateProps);
      if (this.satellitePopup && plan !== 'pro') actions.track('satellite_popup_exposed', gateProps);
      const video = this.renderRoot.querySelector('video');
      if (video && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        video.src = '/satellite-feature-demo.mp4';
        video.muted = true;
        void video.play().catch(() => { /* The play button allows manual playback. */ });
      }
    };
    this.ctaDisabled = false;
    this.checkoutStatus = '';
    if (feature === 'auto_outline') {
      this.headlineText = t('auto_outline.upgrade_title');
      this.headlineHidden = false;
    } else if (feature === 'collaborators' || feature === 'password_protection') {
      this.headlineText = t(feature === 'collaborators' ? 'pro_page.feature_collab_title' : 'pro_page.feature_share_title');
      this.headlineHidden = false;
    } else if (feature === 'map_library') {
      this.headlineText = t('upgrade_dialog.headline_map_library');
      this.headlineHidden = false;
    } else if (feature === 'service_area_widget') {
      this.headlineText = t('upgrade_dialog.headline_service_area_widget');
      this.headlineHidden = false;
    } else if (feature === 'ai_map_builder') {
      this.headlineText = source === 'ai_chat_images' ? t('ai_map_assistant.image_paywall') : t('upgrade_dialog.feature_ai');
      this.headlineHidden = false;
    } else if (feature === 'satellite_basemap') {
      this.headlineText = t('upgrade_dialog.headline_satellite_basemap');
      this.headlineHidden = false;
    } else if (feature === 'drawing_layers') {
      this.headlineText = t('upgrade_dialog.headline_drawing_layers');
      this.headlineHidden = false;
    } else if (feature === 'data_layers') {
      this.headlineText = t('upgrade_dialog.headline_data_layers');
      this.headlineHidden = false;
    } else if (feature === 'illustrated_markers') {
      this.headlineText = t('upgrade_dialog.headline_illustrated_markers');
      this.headlineHidden = false;
    } else if (feature === 'pasted_images') {
      this.headlineText = t('upgrade_dialog.headline_pasted_images');
      this.headlineHidden = false;
    } else {
      this.headlineText = '';
      this.headlineHidden = true;
    }
    if (this._dialog) {
      if (isFunction(this._dialog.showModal)) {
        this._dialog.showModal();
      } else {
        this._dialog.setAttribute('open', '');
      }
      void this.updateComplete.then(() => { this._focusTitle(); this._watchExposure(recordExposure); });
    } else {
      this.updateComplete.then(() => {
        this._dialog = this.renderRoot.querySelector('#upgrade-dialog');
        if (this._dialog) {
          if (isFunction(this._dialog.showModal)) {
            this._dialog.showModal();
          } else {
            this._dialog.setAttribute('open', '');
          }
          this._focusTitle();
          this._watchExposure(recordExposure);
        }
      });
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.exposureObserver?.disconnect();
    this.exposureWatch?.abort();
    this.renderRoot.querySelector('video')?.pause();
  }

  _onCancel(event: Event) {
    event.preventDefault();
    this._onDismiss();
  }

  _onClose() {
    this._trackUpgradePromptDismissed();
    this.close();
  }

  _onDismiss() {
    this._trackUpgradePromptDismissed();
    this.close();
  }

  _onSignin() {
    this.requiredActions.track('premium_existing_customer_signin_clicked', {
      ...this.satelliteAttribution,
      feature: this.activeFeature, source: this.activeSource,
      user_plan: this.requiredActions.getUserPlanForAnalytics() || 'anonymous',
      coupon_active: String(this.upgradeHasCoupon),
    });
    this.close();
    const titleKey = `signin_dialog.title_upgrade_${this.activeFeature}`;
    this.requiredActions.openSigninDialog({
      source: 'upgrade',
      next: `/pro/?${new URLSearchParams({ feature: this.activeFeature, source: this.activeSource })}`,
      title: t(titleKey, t('signin_dialog.title_account')),
      description: t('signin_dialog.description_upgrade'),
    });
  }

  _onBackdropClick(e: Event) {
    if (e.target === this._dialog) {
      this._trackUpgradePromptDismissed();
      this.close();
    }
  }

  private async _startEmbeddedCheckout(
    data: UpgradeCheckoutResponse,
    trackedProps: UpgradeCheckoutProps,
    apiMs: number,
    preparation?: number,
  ): Promise<void> {
    if (data.ui_mode !== 'embedded' || !data.client_secret || !data.publishable_key) {
      throw new Error(t('upgrade_dialog.error_something_wrong'));
    }
    const embeddedCheckout = await this._embeddedCheckoutElement();
    const { track } = this.requiredActions;
    const openedAt = performance.now();
    await embeddedCheckout.open({
      clientSecret: data.client_secret,
      publishableKey: data.publishable_key,
      onMounted: () => track('pro_checkout_embedded_mounted', trackedProps),
      onOpened: () => track('pro_checkout_embedded_opened', trackedProps),
      onClosed: (reason) => {
        this._cancelDatasetSave();
        track('pro_checkout_embedded_closed', { ...trackedProps, reason });
        void recordCheckoutCancellation({
          checkoutAttemptId: data.checkout_attempt_id,
          checkoutCancellationId: data.checkout_cancellation_id,
          checkoutCancellationToken: data.checkout_cancellation_token,
          checkoutCancellationExpiresAt: data.checkout_cancellation_expires_at,
        }, reason);
      },
      onPaymentEvent: (event) => track('pro_checkout_submission', { ...trackedProps, ...event }),
      onExitReason: (reason) => track('pro_checkout_exit_survey_answered', { ...trackedProps, reason }),
      onFailed: (reason) => {
        track('pro_checkout_failed', { ...trackedProps, reason });
        track('pro_checkout_performance', { ...trackedProps, outcome: 'failed', total_ms: checkoutDurationMs(apiMs + performance.now() - openedAt) });
      },
      onRenderUnconfirmed: () => track('pro_checkout_performance', { ...trackedProps, outcome: 'render_unconfirmed', last_step: 'mounted', total_ms: checkoutDurationMs(apiMs + performance.now() - openedAt) }),
      onPerformance: (timing) => track('pro_checkout_performance', {
        ...trackedProps,
        outcome: 'embedded_opened',
        warmup: wasCheckoutWarmupRequested() ? 'requested' : 'cold',
        api_ms: checkoutDurationMs(apiMs),
        stripe_js_ms: checkoutDurationMs(timing.stripeJsMs),
        checkout_create_ms: checkoutDurationMs(timing.checkoutCreateMs),
        checkout_mount_ms: checkoutDurationMs(timing.checkoutMountMs),
        checkout_render_ms: checkoutDurationMs(timing.checkoutRenderMs),
        total_ms: checkoutDurationMs(apiMs + timing.embeddedTotalMs),
      }),
      preparation,
    });
    this.checkoutStatus = '';
    this.ctaDisabled = false;
  }

  private async _embeddedCheckoutElement(): Promise<EmbeddedCheckoutDialogElement> {
    if (!this._embeddedCheckout) {
      const { EmbeddedCheckoutDialogElement } = await loadEmbeddedCheckoutDialogLitModule();
      this._embeddedCheckout = EmbeddedCheckoutDialogElement.init();
    }
    return this._embeddedCheckout;
  }

  private _checkoutProps(
    checkoutAttemptId: string | undefined,
  ): UpgradeCheckoutProps {
    const actions = this.requiredActions;
    const checkoutProps: UpgradeCheckoutProps = {
      ...this.satelliteAttribution,
      feature: this.activeFeature,
      source: this.activeSource,
      user_plan: actions.getUserPlanForAnalytics() || 'anonymous',
      coupon_active: String(this.upgradeHasCoupon),
      checkout_mode: 'subscription',
    };
    if (checkoutAttemptId) checkoutProps.checkout_attempt_id = checkoutAttemptId;
    checkoutProps.checkout_experience_variant = DEFAULT_CHECKOUT_EXPERIENCE;
    return checkoutProps;
  }

  private _checkoutRequestBody(
    checkoutContext: CheckoutAnalyticsContext,
  ): UpgradeCheckoutRequest {
    const actions = this.requiredActions;
    const requestBody: UpgradeCheckoutRequest = {
      ...this.satelliteAttribution,
      feature: this.activeFeature,
      source: this.activeSource,
      user_plan: actions.getUserPlanForAnalytics() || 'anonymous',
      coupon_active: String(this.upgradeHasCoupon),
      checkout_mode: 'subscription',
      ...checkoutContext,
    };
    requestBody.checkout_experience_variant = DEFAULT_CHECKOUT_EXPERIENCE;
    return requestBody;
  }

  private _handleCheckoutFailure(
    checkoutProps: UpgradeCheckoutProps,
    checkoutStartedAt: number,
    cause: unknown,
  ): void {
    const { track } = this.requiredActions;
    track('pro_checkout_failed', { ...checkoutProps, reason: checkoutFailureReason(cause) });
    track('pro_checkout_performance', {
      ...checkoutProps,
      outcome: 'failed',
      warmup: wasCheckoutWarmupRequested() ? 'requested' : 'cold',
      total_ms: checkoutDurationMs(performance.now() - checkoutStartedAt),
    });
    this.checkoutStatus = cause instanceof CheckoutRequestError ? cause.message : t('upgrade_dialog.error_something_wrong');
    this.ctaDisabled = false;
  }

  private _cancelDatasetSave(): void {
    if (!this._datasetSaveReturnTo) return;
    const url = new URL(this._datasetSaveReturnTo, window.location.origin);
    url.searchParams.delete('resume_save');
    rememberCheckoutReturnTo(`${url.pathname}${url.search}${url.hash}`);
    this._datasetSaveReturnTo = null;
  }

  async checkoutDatasetSave(title: string): Promise<void> {
    this._datasetSaveTitle = title;
    this.activeFeature = 'data_layers';
    this.activeSource = 'data_layer_save';
    this.checkoutStatus = '';
    await this._checkout(true);
    if (this.checkoutStatus && !this.ctaDisabled) {
      const message = this.checkoutStatus;
      const pendingReturn = this._datasetSaveReturnTo;
      this.open({ feature: 'data_layers', source: 'data_layer_save' });
      this._datasetSaveReturnTo = pendingReturn;
      this.checkoutStatus = message;
    }
  }

  _onCtaClick() { return this._checkout(); }

  private get satelliteAttribution(): Partial<SatellitePopupAttribution> {
    return this.activeFeature === 'satellite_basemap' ? this.satellitePopup ?? {} : {};
  }

  private get satelliteDemo(): boolean {
    return this.activeFeature === 'satellite_basemap' && this.satellitePopup?.popup_variant === 'feature_demo';
  }

  private _videoOutcome(outcome: 'played' | 'paused' | 'ended' | 'failed'): void {
    this.satelliteVideoPaused = outcome !== 'played';
    if (this.videoOutcomes.has(outcome)) return;
    this.videoOutcomes.add(outcome);
    if (outcome === 'failed') this.satelliteVideoFailed = true;
    this.requiredActions.track('satellite_popup_video', { ...this.satelliteAttribution, feature: 'satellite_basemap', source: this.activeSource, outcome });
  }

  private _toggleSatelliteVideo(): void {
    const video = this.renderRoot.querySelector('video');
    if (!video) return;
    if (video.paused) void video.play().catch(() => this._videoOutcome('failed'));
    else video.pause();
  }

  private _updateSatelliteScene(event: Event): void {
    const video = event.currentTarget;
    if (!(video instanceof HTMLVideoElement)) return;
    this.satelliteVideoScene = Math.min(2, Math.floor(video.currentTime / 5));
  }

  private async _checkout(resumeSave = false) {
    if (this.checkoutActivity !== 'idle') return;
    if (window.drawOnAMapNative) {
      if (this.activeFeature === 'service_area_widget') return;
      if (await openNativePro()) window.location.assign(await this.requiredActions.saveCheckoutDraft());
      return;
    }
    const actions = this.requiredActions;
    const checkoutContext = createCheckoutAnalyticsContext();
    const checkoutProps = this._checkoutProps(checkoutContext.checkout_attempt_id);
    actions.track('premium_upgrade_cta_clicked', checkoutProps);
    actions.track('checkout_clicked', checkoutProps);
    this.ctaDisabled = true;
    this.checkoutStatus = t('upgrade_dialog.redirecting');
    const checkoutStartedAt = performance.now();
    const embeddedCheckoutPromise = this._embeddedCheckoutElement();
    const attempt = new CheckoutPreparation();
    let preparation: number | undefined;
    try {
      const returnTo = await checkoutDeadline(actions.saveCheckoutDraft());
      if (resumeSave === true || this._datasetSaveReturnTo) {
        this._datasetSaveReturnTo = datasetSaveReturnTo(returnTo, 'payment');
        rememberCheckoutReturnTo(this._datasetSaveReturnTo);
      }
      const requestBody = this._checkoutRequestBody(checkoutContext);
      if (this.activeFeature === 'collaborators' || this.activeFeature === 'password_protection') requestBody.return_to = returnTo;
      const responsePromise = fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: await proCheckoutHeaders(),
        body: JSON.stringify(requestBody)
      });
      const embeddedCheckout = await embeddedCheckoutPromise;
      preparation = attempt.prepare(onClosed => embeddedCheckout.prepare({
        title: this._datasetSaveReturnTo ? this._datasetSaveTitle : undefined, onClosed,
      }));
      this.renderRoot.querySelector('video')?.pause();
      const res = await checkoutDeadline(responsePromise);
      const payload = await checkoutDeadline(res.json());
      if (redirectCheckoutResponse(res.status, payload)) return;
      const data = upgradeCheckoutResponse(payload);
      if (!res.ok) throw new CheckoutRequestError(data);
      const apiMs = performance.now() - checkoutStartedAt;
      const trackedProps = data.checkout_attempt_id
        ? { ...checkoutProps, checkout_attempt_id: data.checkout_attempt_id }
        : checkoutProps;
      if (attempt.closeReason) {
        this._cancelDatasetSave();
        actions.track('pro_checkout_embedded_closed', { ...trackedProps, reason: attempt.closeReason });
        void recordCheckoutCancellation({
          checkoutAttemptId: data.checkout_attempt_id,
          checkoutCancellationId: data.checkout_cancellation_id,
          checkoutCancellationToken: data.checkout_cancellation_token,
          checkoutCancellationExpiresAt: data.checkout_cancellation_expires_at,
        }, attempt.closeReason);
        this.checkoutStatus = '';
        this.ctaDisabled = false;
        return;
      }
      await this._startEmbeddedCheckout(data, trackedProps, apiMs, preparation);
    } catch (error) {
      console.error('Checkout could not open:', error);
      if (preparation !== undefined) this._embeddedCheckout?.failPreparation(preparation, error instanceof CheckoutRequestError ? error.message : undefined);
      this._handleCheckoutFailure(checkoutProps, checkoutStartedAt, error);
    }
  }

  private _satellitePreview(): TemplateResult | string {
    if (this.activeFeature !== 'satellite_basemap') return '';
    if (this.satelliteDemo) {
      const still = this.satelliteVideoFailed || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return html`<figure class="dlg:m-[0_28px_14px] dlg:[@media_(max-width:_768px)]:m-[0_20px_12px]">
        ${still ? html`<img class="dlg:block dlg:w-[100%] dlg:max-w-[min(100%,_54vh)] dlg:mx-auto dlg:h-[auto] dlg:rounded-[12px]" src="/satellite-feature-demo.jpg" width="960" height="360" alt=${t('satellite_popup.video_description')}>`
          : html`<div class="dlg:relative dlg:max-w-[min(100%,_54vh)] dlg:mx-auto"><video class="dlg:block dlg:w-[100%] dlg:max-w-[min(100%,_54vh)] dlg:mx-auto dlg:h-[auto] dlg:rounded-[12px] dlg:[background:#152d28]" width="960" height="360" autoplay loop muted playsinline preload="none" poster="/satellite-feature-demo.jpg" aria-label=${t('satellite_popup.video_description')} @timeupdate=${this._updateSatelliteScene} @playing=${() => this._videoOutcome('played')} @pause=${() => this._videoOutcome('paused')} @ended=${() => this._videoOutcome('ended')} @error=${() => this._videoOutcome('failed')}></video>
            <div class="dlg:absolute dlg:inset-0 dlg:rounded-[12px] dlg:pointer-events-none dlg:[background:linear-gradient(180deg,transparent_45%,rgba(4,19,22,.65))]"><span data-satellite-scene=${this.satelliteVideoScene} class="dlg:absolute dlg:left-[16px] dlg:bottom-[20px] dlg:max-w-[85%] dlg:text-white dlg:font-[650] dlg:[font-size:clamp(13px,2vw,20px)] dlg:[line-height:1.2] dlg:[text-shadow:0_1px_8px_rgba(0,0,0,.5)]">${t(`satellite_popup.scene_${this.satelliteVideoScene}`)}</span></div>
            <button type="button" class="dlg:absolute dlg:top-[10px] dlg:right-[10px] dlg:w-[32px] dlg:h-[32px] dlg:rounded-full dlg:[background:rgba(0,0,0,.55)] dlg:text-white dlg:border-0 dlg:cursor-pointer" aria-label=${t(this.satelliteVideoPaused ? 'satellite_popup.play_animation' : 'satellite_popup.pause_animation')} @click=${this._toggleSatelliteVideo}><span aria-hidden="true">${this.satelliteVideoPaused ? '▶' : 'Ⅱ'}</span></button>
          </div>`}
        <figcaption class="dlg:mt-[6px] dlg:text-[11px] dlg:[color:var(--text-secondary)]">${t('satellite_popup.video_caption')}</figcaption>
      </figure>`;
    }
    return html`
      <figure class="upgrade-satellite-preview dlg:[aspect-ratio:15_/_4] dlg:m-[0_28px_14px] dlg:[overflow:hidden] dlg:[border:1px_solid_var(--border-color)] dlg:rounded-[14px] dlg:[background:var(--bg-toolbar)] dlg:[&_img]:block dlg:[&_img]:w-[100%] dlg:[&_img]:h-[100%] dlg:[&_img]:object-cover dlg:[@media_(max-width:_768px)]:m-[0_20px_12px]">
        <img
          src="/satellite-upgrade-preview.jpg"
          width="1800"
          height="480"
          alt=${t('upgrade_dialog.benefit_satellite_basemap')}
        >
      </figure>
    `;
  }

  render(): TemplateResult {
    const isServiceAreaWidget = this.activeFeature === 'service_area_widget';
    const isMapLibrary = this.activeFeature === 'map_library';
    const priceDisplay = isServiceAreaWidget
      ? html`${t('upgrade_dialog.price_service_area_widget')}`
      : html`${t('upgrade_dialog.price_standard')}`;
    let ctaLabel = t('upgrade_dialog.cta_standard');
    if (isServiceAreaWidget) ctaLabel = t('upgrade_dialog.cta_service_area_widget');
    else if (isMapLibrary) ctaLabel = t('upgrade_dialog.cta_map_library');
    else if (this.activeFeature === 'satellite_basemap') ctaLabel = this.satelliteDemo ? t('satellite_popup.cta') : t('upgrade_dialog.cta_satellite_basemap');
    else if (this.activeFeature === 'drawing_layers') ctaLabel = t('upgrade_dialog.cta_layers');
    else if (this.activeFeature === 'data_layers') ctaLabel = t('upgrade_dialog.cta_data_layers');
    else if (this.activeFeature === 'ai_map_builder') ctaLabel = t('upgrade_dialog.cta_ai_map_builder');
    const planName = isServiceAreaWidget
      ? t('upgrade_dialog.feature_service_area_widget')
      : t('upgrade_dialog.pro_badge');
    const title = this.satelliteDemo ? t('satellite_popup.title') : isMapLibrary
      ? t('save.save_map')
      : this.headlineText || t('upgrade_dialog.pro_badge');
    let subtitle = t(isMapLibrary && this.activeSource === 'map_limit' ? 'upgrade_dialog.subtitle_map_library' : 'upgrade_dialog.subtitle_standard');
    if (this.activeFeature === 'auto_outline') {
      subtitle = t('auto_outline.upgrade_detail');
    } else if (this.activeFeature === 'collaborators' || this.activeFeature === 'password_protection') {
      subtitle = t(this.activeFeature === 'collaborators' ? 'pro_page.feature_collab_description' : 'pro_page.feature_share_description');
      ctaLabel = t('share.upgrade_continue');
    } else if (this.activeFeature === 'drawing_layers') {
      subtitle = t('upgrade_dialog.feature_layers_detail').replace(/^:\s*/u, '');
    } else if (this.activeFeature === 'ai_map_builder') {
      subtitle = this.activeSource === 'ai_chat_images' ? t('ai_map_assistant.image_paywall_detail') : t('upgrade_dialog.feature_ai_detail').replace(/^:\s*/u, '');
    } else if (this.activeFeature === 'service_area_widget') {
      subtitle = t('upgrade_dialog.feature_service_area_widget_detail').replace(/^:\s*/u, '');
    } else if (this.activeFeature === 'illustrated_markers') {
      subtitle = t('upgrade_dialog.subtitle_illustrated_markers');
    } else if (this.activeFeature === 'pasted_images') {
      subtitle = t('upgrade_dialog.subtitle_pasted_images');
    } else if (this.activeFeature === 'satellite_basemap') {
      subtitle = this.satelliteDemo ? t('satellite_popup.description') : t('upgrade_dialog.subtitle_satellite_basemap');
    }
    const activeBenefit = this.activeFeature === 'drawing_layers' || this.activeFeature === 'data_layers'
      ? 'layers'
      : this.activeFeature;
    const benefits = [
      { feature: 'satellite_basemap', label: t('upgrade_dialog.benefit_satellite_basemap') },
      { feature: 'layers', label: t('upgrade_dialog.feature_layers') },
      { feature: 'map_library', label: t('pro_page.included_saved') },
      { feature: 'png_export', label: t('upgrade_dialog.benefit_exports') },
      { feature: 'ai_map_builder', label: t('upgrade_dialog.benefit_ai') },
      { feature: 'private_links', label: t('pro_page.included_private') },
      { feature: 'collaborators', label: t('pro_page.included_collab') },
    ];
    if (this.activeFeature === 'auto_outline') benefits.unshift({ feature: 'auto_outline', label: t('auto_outline.title') });
    if (this.activeFeature === 'illustrated_markers') benefits.unshift({ feature: 'illustrated_markers', label: t('upgrade_dialog.benefit_illustrated_markers') });
    if (isServiceAreaWidget) {
      benefits.unshift({ feature: 'service_area_widget', label: t('upgrade_dialog.feature_service_area_widget') });
    }
    const activeBenefitItem = benefits.find(({ feature }) => feature === activeBenefit);
    const orderedBenefits = activeBenefitItem
      ? [activeBenefitItem, ...benefits.filter(({ feature }) => feature !== activeBenefit)]
      : benefits;

    return html`
      <dialog id="upgrade-dialog" class="share-dialog upgrade-dialog ${this.satelliteDemo ? 'satellite-demo' : ''} dlg:[&.upgrade-dialog.satellite-demo]:max-h-[min(92vh,_820px)] dlg:supports-[height:100dvh]:[&.upgrade-dialog.satellite-demo]:max-h-[min(92dvh,_820px)] dlg:[&.upgrade-dialog]:max-w-[500px] dlg:[&.upgrade-dialog]:max-h-[min(90vh,_720px)] dlg:supports-[height:100dvh]:[&.upgrade-dialog]:max-h-[min(90dvh,_720px)] dlg:[@media_(max-width:_768px)]:[&.upgrade-dialog]:max-h-[92vh] dlg:supports-[height:100dvh]:[@media_(max-width:_768px)]:[&.upgrade-dialog]:max-h-[92dvh] mchrome:w-max-768:w-[100%] mchrome:w-max-768:max-w-[100%] mchrome:w-max-768:inset-[auto_0_0] mchrome:w-max-768:[transform:none] mchrome:w-max-768:rounded-[16px_16px_0_0] mchrome:w-max-768:max-h-[85vh] mchrome:supports-[height:100dvh]:w-max-768:max-h-[85dvh] dlg:[border:none] dlg:rounded-[16px] dlg:p-[0] dlg:max-w-[420px] dlg:w-[calc(100%_-_32px)] dlg:fixed dlg:right-[auto] dlg:bottom-[auto] dlg:top-[50%] dlg:left-[50%] dlg:m-[0] dlg:max-h-[min(70vh,_520px)] dlg:supports-[height:100dvh]:max-h-[min(70dvh,_520px)] dlg:h-[auto] dlg:[background:var(--bg-toolbar)] dlg:[color:var(--text-primary)] dlg:[box-shadow:0_24px_80px_rgb(0_0_0_/_25%)] dlg:[transform:translate(-50%,_-50%)] dlg:[&::backdrop]:[background:rgb(0_0_0_/_50%)] dlg:[&::backdrop]:[backdrop-filter:blur(4px)]" aria-labelledby="upgrade-dialog-title" @click=${this._onBackdropClick} @cancel=${this._onCancel}>
        <div class="upgrade-dialog-shell ${this.satelliteDemo ? 'satellite-demo-shell' : ''} dlg:[&.satellite-demo-shell]:max-h-[min(92vh,_820px)] dlg:supports-[height:100dvh]:[&.satellite-demo-shell]:max-h-[min(92dvh,_820px)] dlg:max-h-[min(90vh,_720px)] dlg:supports-[height:100dvh]:max-h-[min(90dvh,_720px)] dlg:[overflow-y:auto] dlg:[@media_(max-width:_768px)]:max-h-[92vh] dlg:supports-[height:100dvh]:[@media_(max-width:_768px)]:max-h-[92dvh]">
          <header class="upgrade-dialog-header dlg:relative dlg:p-[26px_28px_16px] dlg:[&_h2]:m-[0_44px_6px_0] dlg:[&_h2]:[color:var(--text-primary)] dlg:[&_h2]:text-[26px] dlg:[&_h2]:font-[750] dlg:[&_h2]:tracking-[-0.025em] dlg:[&_h2]:leading-[1.1] dlg:[&_h2:focus]:[outline:none] dlg:[&_p]:m-[0] dlg:[&_p]:[color:var(--text-secondary)] dlg:[&_p]:text-[14px] dlg:[&_p]:leading-[1.5] dlg:[@media_(max-width:_768px)]:p-[22px_20px_14px] dlg:[@media_(max-width:_768px)]:[&_h2]:text-[24px]">
            <button class="feedback-dialog-close upgrade-dialog-close dlg:absolute dlg:top-[20px] dlg:right-[20px] dlg:[@media_(max-width:_768px)]:top-[14px] dlg:[@media_(max-width:_768px)]:right-[14px] dlg:w-[32px] dlg:h-[32px] dlg:[border:none] dlg:[background:var(--bg-secondary)] dlg:rounded-[8px] dlg:[cursor:pointer] dlg:flex dlg:items-center dlg:justify-center dlg:[color:var(--text-muted)] dlg:[transition:all_0.15s_ease] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:var(--bg-hover)] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[color:var(--text-secondary)] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] confirm:[confirm-dialog-element_&]:w-[32px] confirm:[confirm-dialog-element_&]:h-[32px] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:[background:var(--bg-secondary,_#334155)] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:flex confirm:[confirm-dialog-element_&]:items-center confirm:[confirm-dialog-element_&]:justify-center confirm:[confirm-dialog-element_&]:[color:var(--text-muted,_#94a3b8)] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:var(--bg-hover,_#475569)] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[color:var(--text-secondary,_#cbd5e1)]" id="upgrade-close" title="${t('upgrade_dialog.close')}" type="button" aria-label="${t('upgrade_dialog.close')}" @click=${this._onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <h2 id="upgrade-dialog-title" tabindex="-1" autofocus>${title}</h2>
            ${this.activeFeature === 'data_layers' ? html`
              <div class="upgrade-data-limits dlg:grid dlg:gap-[8px] dlg:mt-[14px]">
                <p class="dlg:p-[10px_12px] dlg:rounded-[8px] dlg:[background:var(--bg-secondary)]">${t('upgrade_dialog.data_limits_free')}</p>
                <p class="dlg:p-[10px_12px] dlg:rounded-[8px] dlg:[background:color-mix(in_srgb,#16a34a_10%,var(--bg-toolbar))] dlg:[border:1px_solid_color-mix(in_srgb,#16a34a_25%,transparent)] dlg:font-[600]">${t('upgrade_dialog.data_limits_pro')}</p>
              </div>
            ` : html`<p>${subtitle}</p>`}
          </header>

          ${this._satellitePreview()}

          <section class="upgrade-plan-card dlg:m-[0_28px_14px] dlg:p-[20px] dlg:[border:1px_solid_var(--border-color)] dlg:rounded-[14px] dlg:[background:var(--bg-toolbar)] dlg:[box-shadow:0_10px_28px_rgb(15_23_42_/_6%)] dlg:[@media_(max-width:_768px)]:m-[0_20px_12px] dlg:[@media_(max-width:_768px)]:p-[16px]" aria-labelledby="upgrade-plan-name">
            <div class="upgrade-plan-heading dlg:flex dlg:items-center dlg:justify-between dlg:gap-[16px] dlg:pb-[14px] dlg:[border-bottom:1px_solid_var(--border-color)] dlg:[&_h3]:m-[0] dlg:[&_h3]:[color:var(--text-primary)] dlg:[&_h3]:text-[18px] dlg:[&_h3]:font-[750] dlg:[&_p]:m-[0] dlg:[&_p]:[color:var(--text-primary)] dlg:[&_p]:text-[20px] dlg:[&_p]:font-[800] dlg:[&_p]:tracking-[-0.02em]">
              <h3 id="upgrade-plan-name">${planName}</h3>
              <p id="upgrade-price-display">${priceDisplay}</p>
            </div>
            ${this.satelliteDemo ? html`<p class="dlg:m-[10px_0_0] dlg:text-[12px] dlg:[color:var(--text-secondary)]">${t('dataset_save.billing')}</p>` : ''}
            <p class="upgrade-includes-label dlg:m-[14px_0_10px] dlg:[color:var(--text-secondary)] dlg:text-[12px] dlg:font-[700]">${isServiceAreaWidget
              ? t('upgrade_dialog.service_area_widget_includes_pro')
              : t('pro_page.features_heading')}</p>
            <ul class="upgrade-benefits dlg:grid dlg:grid-cols-[repeat(2,_minmax(0,_1fr))] dlg:gap-[10px_16px] dlg:m-[0] dlg:p-[0] dlg:[list-style:none] dlg:[&_li]:flex dlg:[&_li]:items-center dlg:[&_li]:gap-[8px] dlg:[&_li]:[color:var(--text-primary)] dlg:[&_li]:text-[12px] dlg:[&_li]:leading-[1.35] dlg:[@media_(max-width:_768px)]:grid-cols-[1fr] dlg:[@media_(max-width:_768px)]:gap-[9px]">
              ${orderedBenefits.map(({ feature, label }) => html`
                <li class=${feature === activeBenefit ? 'upgrade-benefit-active dlg:font-[700]' : ''} data-upgrade-feature=${feature}>
                  <span class="upgrade-benefit-check ${feature === activeBenefit ? 'upgrade-benefit-check-active dlg:[background:#16a34a]' : 'dlg:[background:var(--text-primary)]'} dlg:inline-flex dlg:[flex:0_0_18px] dlg:items-center dlg:justify-center dlg:w-[18px] dlg:h-[18px] dlg:rounded-[50%] dlg:[color:#fff] dlg:text-[11px] dlg:font-[800]" aria-hidden="true">✓</span>
                  <span>${label}</span>
                </li>
              `)}
            </ul>
            <button id="upgrade-cta" class="feedback-btn feedback-btn-primary upgrade-cta dlg:w-[100%] dlg:min-h-[46px] dlg:mt-[18px] dlg:p-[12px_16px] dlg:text-[14px] dlg:[border:none] dlg:rounded-[8px] dlg:p-[9px_14px] dlg:text-[13px] dlg:font-[600] dlg:[cursor:pointer] dlg:[transition:all_0.15s_ease] dlg:[&:disabled]:[opacity:0.55] dlg:[&:disabled]:[cursor:default] dlg:[#magic-link-submit:where(&)]:mt-[12px] dlg:[background:#15803d] dlg:[color:#fff] dlg:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[background:#166534] confirm:[confirm-dialog-element_&]:[border:none] confirm:[confirm-dialog-element_&]:rounded-[8px] confirm:[confirm-dialog-element_&]:p-[9px_14px] confirm:[confirm-dialog-element_&]:text-[13px] confirm:[confirm-dialog-element_&]:font-[600] confirm:[confirm-dialog-element_&]:[cursor:pointer] confirm:[confirm-dialog-element_&]:[transition:all_0.15s_ease] confirm:[confirm-dialog-element_&]:[background:#15803d] confirm:[confirm-dialog-element_&]:[color:#fff] confirm:[@media_(hover:_hover)_and_(pointer:_fine)]:[confirm-dialog-element_&:hover]:[background:#166534]" type="button" ?disabled=${this.ctaDisabled} @click=${this._onCtaClick}>${ctaLabel}</button>
            <p class="upgrade-trust dlg:m-[8px_0_0] dlg:[color:var(--text-muted)] dlg:text-[11px] dlg:leading-[1.4] dlg:text-center">${t('pro_page.trust')}</p>
            ${this.satelliteDemo && !this.ctaDisabled && !this.checkoutStatus ? '' : html`<p id="upgrade-checkout-status" class="upgrade-checkout-status dlg:m-[8px_0_0] dlg:[color:var(--text-muted)] dlg:text-[11px] dlg:leading-[1.4] dlg:text-center dlg:min-h-[15px] dlg:mt-[2px] dlg:[&:not(:empty)]:flex dlg:[&:not(:empty)]:items-center dlg:[&:not(:empty)]:justify-center dlg:[&:not(:empty)]:gap-[8px] dlg:[&:not(:empty)]:min-h-[30px] dlg:[&:not(:empty)]:[color:var(--text-primary)] dlg:[&:not(:empty)]:text-[13px] dlg:[&:not(:empty)]:font-[600]" role="status" aria-live="polite">
              ${this.ctaDisabled ? html`<span class="upgrade-checkout-spinner dlg:w-[18px] dlg:h-[18px] dlg:[flex:0_0_auto] dlg:[border:3px_solid_rgb(20_184_166_/_20%)] dlg:[border-top-color:#0f766e] dlg:rounded-[50%] dlg:[animation:upgrade-checkout-spin_0.8s_linear_infinite] dlg:[@media_(prefers-reduced-motion:_reduce)]:[animation:none] dlg:[@media_(prefers-reduced-motion:_reduce)]:[border-top-color:rgb(20_184_166_/_20%)] dlg:[@media_(prefers-reduced-motion:_reduce)]:[border-right-color:#0f766e]" aria-hidden="true"></span>` : ''}
              <span>${this.checkoutStatus}</span>
            </p>`}

          </section>

          ${this.satelliteDemo ? html`<button id="upgrade-dismiss" class="upgrade-dismiss dlg:[border:none] dlg:[background:none] dlg:[color:var(--text-secondary)] dlg:[cursor:pointer] dlg:[font-family:inherit] dlg:block dlg:m-[0_auto_14px] dlg:p-[6px_12px] dlg:text-[12px] dlg:font-[500]" type="button" @click=${this._onDismiss}>${t('satellite_popup.keep_editing')}</button>` : null}

          <footer class="upgrade-dialog-footer dlg:[&_button]:[border:none] dlg:[&_button]:[background:none] dlg:[&_button]:[color:var(--text-primary)] dlg:[&_button]:[cursor:pointer] dlg:[&_button]:[font-family:inherit] dlg:[&_button]:p-[0] dlg:[&_button]:text-[length:inherit] dlg:[&_button]:[text-decoration:underline] dlg:[&_button]:[text-underline-offset:3px] dlg:p-[15px_28px_17px] dlg:[border-top:1px_solid_var(--border-color)] dlg:[&_p]:m-[0] dlg:[&_p]:[color:var(--text-secondary)] dlg:[&_p]:text-[11px] dlg:[&_p]:text-center dlg:[@media_(max-width:_768px)]:p-[14px_20px_16px]">
            <p>${t('upgrade_dialog.already_pro')} <button id="upgrade-signin" type="button" @click=${this._onSignin}>${t('upgrade_dialog.sign_in')}</button></p>
          </footer>
        </div>
      </dialog>
    `;
  }
}

customElements.define('upgrade-dialog-element', UpgradeDialogElement);
